import { useState } from 'react'
import { Plus } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
  StatusBadge,
  useToast,
} from '@/components/ui'
import { useDeleteTariff, useTariffs } from '../api/contractsApi.ts'
import type { Tariff } from '../types.ts'
import { TariffFormModal } from '../components/TariffFormModal.tsx'
import { TariffEditor } from '../components/TariffEditor.tsx'

interface TariffsSectionProps {
  productId: string
  productUnit: string
}

function formatDate(d: string | null): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('de-DE')
}

const CYCLE_LABELS: Record<Tariff['billingCycle'], string> = {
  monthly: 'Monatlich',
  quarterly: 'Quartalsweise',
  yearly: 'Jährlich',
  once: 'Einmalig',
}

export function TariffsSection({ productId, productUnit }: TariffsSectionProps) {
  const { toast } = useToast()
  const { data: tariffs = [], isLoading, error, refetch } = useTariffs({ productId })
  const del = useDeleteTariff()
  const [createOpen, setCreateOpen] = useState(false)
  const [openTariff, setOpenTariff] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Tariff | null>(null)

  if (isLoading) return <LoadingState label="Lade Tarife …" />
  if (error) {
    return (
      <ErrorState
        description="Tarife konnten nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => void refetch()}
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-small text-gray-600">
          {tariffs.length} Tarif{tariffs.length === 1 ? '' : 'e'} für dieses Produkt
        </p>
        <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
          Neuer Tarif
        </Button>
      </div>

      {tariffs.length === 0 ? (
        <EmptyState
          title="Noch kein Tarif angelegt"
          description="Tarife enthalten Komponenten mit Preisen und optionalen Mengenstaffeln. Sie sind versioniert über Gültig-ab/bis."
          action={
            <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
              Neuer Tarif
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {tariffs.map((t) => {
            const isOpen = openTariff === t.id
            return (
              <Card key={t.id}>
                <Card.Header className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setOpenTariff(isOpen ? null : t.id)}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-body font-semibold text-gray-900">{t.name}</h3>
                      <Badge variant="neutral" size="sm">{CYCLE_LABELS[t.billingCycle]}</Badge>
                      <StatusBadge status={t.active ? 'active' : 'inactive'} />
                    </div>
                    <p className="text-caption text-gray-500 mt-0.5">
                      Gültig ab {formatDate(t.validFrom)} {t.validTo ? `bis ${formatDate(t.validTo)}` : '(unbefristet)'}
                      {' · '}{t.currency}
                    </p>
                  </button>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setOpenTariff(isOpen ? null : t.id)}>
                      {isOpen ? 'Zuklappen' : 'Bearbeiten'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(t)}>
                      Löschen
                    </Button>
                  </div>
                </Card.Header>
                {isOpen ? (
                  <Card.Body>
                    <TariffEditor tariffId={t.id} productUnit={productUnit} />
                  </Card.Body>
                ) : null}
              </Card>
            )
          })}
        </div>
      )}

      <TariffFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        productId={productId}
        onCreated={(t) => {
          setCreateOpen(false)
          setOpenTariff(t.id)
        }}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Tarif löschen?"
        message={
          confirmDelete
            ? `„${confirmDelete.name}" wird gelöscht. Wenn der Tarif in einem Vertrag referenziert ist, wird das Löschen abgelehnt.`
            : ''
        }
        variant="danger"
        confirmLabel="Löschen"
        loading={del.isPending}
        onConfirm={() => {
          if (!confirmDelete) return
          del.mutate(confirmDelete.id, {
            onSuccess: () => {
              toast({ title: 'Tarif gelöscht', tone: 'success' })
              setConfirmDelete(null)
            },
            onError: (err) => {
              toast({
                title: 'Löschen nicht möglich',
                description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                tone: 'danger',
              })
            },
          })
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}
